import axios from "axios";
import AuthService from "services/auth.service";
import { normalizeApiError } from "services/apiError";
import type { CareMount } from "types/setting-schema";

interface Pagination {
    next?: {
        page: number;
        limit: number;
    };
    prev?: {
        page: number;
        limit: number;
    };
    total: number;
    page: number;
    pages: number;
    count: number;
}

interface CMSPagination {
    page: number;
    pageSize: number;
    pageCount: number;
    total: number;
}

interface Error {
    status: string;
    name: string;
    message: string;
    details: string;
}

export interface ThirdPartySyncResult {
    provider: "care-mqtt";
    triggered: boolean;
    host?: string;
    results: Array<{
        provider: "care-mqtt";
        status: "updated" | "skipped" | "failed";
        serialNumber?: string;
        deviceId?: string;
        roomName?: string;
        floorPlanRoomId?: string;
        reason?: string;
        error?: string;
        boundaryBox?: number[];
        sensorPosition?: number[];
        mount?: CareMount;
    }>;
}

export interface Response<T> {
    success: boolean;
    count?: number;
    pagination?: Pagination;
    error?: string;
    data?: T;
    thirdPartySync?: ThirdPartySyncResult;
}

export interface CMSResponse<T> {
    meta: CMSPagination;
    error?: Error;
    data?: T;
}

const trimTrailingSlashes = (value: string): string => value.replace(/\/+$/, "");

const getDefaultApiBaseUrl = (): string => {
    if (process.env.REACT_APP_ENV === "production") {
        return "https://api.wecaredynamics.com";
    }

    if (process.env.REACT_APP_ENV === "development") {
        return "https://devapi.wecaredynamics.com";
    }

    return "http://localhost:8080";
};

const normalizeApiBaseUrl = (value?: string): string => {
    const fallback = getDefaultApiBaseUrl();
    const rawValue = value?.trim();

    if (!rawValue) {
        return fallback;
    }

    return trimTrailingSlashes(rawValue).replace(/\/api\/v1$/i, "");
};

export const getApiBaseUrl = (): string => normalizeApiBaseUrl(process.env.REACT_APP_API_BASE_URL);
export const getSocketBaseUrl = (): string =>
    normalizeApiBaseUrl(process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_API_BASE_URL);

const instance = axios.create({
    baseURL: getApiBaseUrl(),
});

// const instance = axios.create({
//     baseURL: process.env.REACT_APP_ENV === "production" ? "https://devapi.realeasyliving.com/" : "http://localhost:6000/",
// });

if (process.env.REACT_APP_ENV === "production") {
    instance.defaults.withCredentials = true;
}

// Add request interceptor
instance.interceptors.request.use(function (config) {
    const token = AuthService.getAuthToken();
    if (token !== null) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers["Content-Type"] = "application/json";
    return config;
});

// Add a response interceptor
instance.interceptors.response.use(
    function (response) {
        return response;
    },
    function (error) {
        const apiError = normalizeApiError(error);
        if (apiError.status === 401) {
            AuthService.logout();
        }
        return Promise.reject(apiError);
    },
);

export const CMSinstance = axios.create({
    baseURL: "https://cms.realeasyliving.com/api/",
});

// Add request interceptor
CMSinstance.interceptors.request.use(function (config) {
    config.headers["Content-Type"] = "application/json";
    return config;
});

export default instance;
